// The Types list (docs/SPEC.md item 6; web/controls.js renderTypeList): every type with its emoji, your interests
// first, then the rest by how many events they have, hidden ones last (RULES.typeOrder). Tap a type to toggle it in
// the filter; the top row picks All interests; Reset (floating at the bottom while something's picked) clears it and
// closes the list. Each row's ★ and "…" come from the profile (TypeRowControls).
import type { RefObject } from "react";
import { SHARED, T } from "../../shared/config";
import { Icon } from "../../shared/icons";
import { RULES } from "../../shared/rules";
import { tagEmoji, useFeed } from "../../state/feed";
import { useProfileView } from "../../state/profile";
import { resetTypes, setPopover, toggleAllInterests, toggleType, useView } from "../../state/view";
import { Popover } from "../../ui/Popover";
import { TypeRowControls, useTypeLevelPress } from "../profile/slots";
import { ResetPill } from "./FilterChip";
import styles from "./TypesPopover.module.css";

export function TypesPopover({ anchor, ordered, leaving, onGone }: {
  anchor: RefObject<HTMLButtonElement | null>; ordered: string[]; leaving: boolean; onGone: () => void;
}) {
  const f = useView((s) => s.filter), tags = useFeed((s) => s.tags), levels = useProfileView().levels;
  const count = new Map(tags.map((t) => [t.name, t.count]));
  const all = f.allInterests;
  return (
    <Popover anchor={anchor} id="type-pop" className={styles.pop} leaving={leaving} onGone={onGone} label={T("filter.anyType")}>
      <div className={styles.list} role="menu">
        <button type="button" id="type-all" role="menuitemcheckbox" aria-checked={all} className={`${styles.toggle} ${styles.all}`}
          onClick={toggleAllInterests}>
          <span className={`${styles.emoji} ${styles.star}`} aria-hidden="true"><Icon name="star-filled" /></span>
          <span className={styles.name}>{T("filter.allInterests")}</span>
          <span className={styles.check} aria-hidden="true">{all && <Icon name="check" />}</span>
        </button>
        {ordered.map((t) => <TypeRow key={t} tag={t} on={!all && f.tags.has(RULES.norm(t))}
          level={levels.get(RULES.norm(t)) ?? null} count={count.get(t)} />)}
      </div>
      <ResetPill id="type-reset" floating shown={all || f.tags.size > 0} onClick={() => { resetTypes(); setPopover(null); }} />
    </Popover>
  );
}

// One type: tap toggles it in the filter; long-press (or the "…", TypeRowControls) opens its level menu.
function TypeRow({ tag: t, on, level, count }: { tag: string; on: boolean; level: string | null; count?: number }) {
  const press = useTypeLevelPress(t);
  return (
    <div className={`${styles.row}${level === "hidden" ? ` ${styles.hidden}` : ""}`} data-type={t} data-type-row="">
      <button type="button" role="menuitemcheckbox" aria-checked={on} className={styles.toggle} {...press.handlers}
        onClick={() => { if (!press.pressed()) toggleType(t); }} onContextMenu={(ev) => ev.preventDefault()}>
        <span className={styles.emoji} aria-hidden="true">{tagEmoji(t) || SHARED.fallbackEmoji}</span>
        <span className={styles.name}>{t}</span>
        {level === "hidden" && <span className={styles.level}><Icon name="eye-off" /></span>}
        <span className={styles.count}>{count ?? ""}</span>
        <span className={styles.check} aria-hidden="true">{on && <Icon name="check" />}</span>
      </button>
      <TypeRowControls tag={t} />
    </div>
  );
}
